import React, { useState } from "react";
import { Clock, Plus, Trash2 } from "lucide-react";
import { NativePickerField, ampm } from "./dailyScheduleShared";
import { VisitDialog } from "./DailyScheduleVisitModals";

// Edit dialogs opened from the Daily Schedule centre table (AdminDailySchedule.jsx). Both save to the
// Balagruha itself with PATCH /api/v1/balagruha/:id/schedule:
// - Available Time & Date = the Balagruha form's "Available Class Timing" rows, so the two stay the same;
// - Total Visits = a number the admin types in (blank = count the visits in the calendar again).
// `onSave(changes)` resolves to an error message, or to nothing when it saved.

const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const EMPTY_ROW = { day: "", toDay: "", startTime: "", endTime: "" };
const MAX_TOTAL_VISITS = 999;

// Stored rows may say "monday" or "Mon"; the pickers need the full name.
const fullDay = (value) => WEEK_DAYS.find((d) => d.slice(0, 3).toLowerCase() === String(value || "").trim().slice(0, 3).toLowerCase()) || "";
// "4:30" -> "04:30", the value a time input expects.
const timeValue = (value) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value || "").trim());
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
};
const rowsOf = (timings) => (Array.isArray(timings) && timings.length
  ? timings.map((t) => {
    const day = fullDay(t?.day);
    const toDay = fullDay(t?.toDay);
    return { day, toDay: WEEK_DAYS.indexOf(toDay) > WEEK_DAYS.indexOf(day) ? toDay : "", startTime: timeValue(t?.startTime), endTime: timeValue(t?.endTime) };
  })
  : [EMPTY_ROW]);

// The same checks the API makes, so the admin sees them before saving.
const timingsProblem = (rows) => {
  for (const [i, row] of rows.entries()) {
    const which = rows.length > 1 ? ` in row ${i + 1}` : "";
    if (!row.day || !row.startTime || !row.endTime) return `Pick the day, start time and end time${which}.`;
    if (row.endTime <= row.startTime) return `The end time must be after the start time${which}.`;
  }
  return "";
};

function CentreSummary({ centre, children }) {
  return (
    <p className="ds-reschedule__current">
      <b>{centre.name}</b>
      {children}
    </p>
  );
}

export function ClassTimingsModal({ centre, onClose, onSave }) {
  const [rows, setRows] = useState(() => rowsOf(centre.classTimings));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const edit = (next) => { setError(""); setRows(next); };
  const update = (index, changes) => edit(rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  // A To day must come after the From day; picking a later From day clears one that no longer does.
  const setDay = (index, day) => update(index, {
    day,
    ...(WEEK_DAYS.indexOf(rows[index].toDay) <= WEEK_DAYS.indexOf(day) && { toDay: "" }),
  });
  const remove = (index) => edit(rows.length > 1 ? rows.filter((_, i) => i !== index) : [EMPTY_ROW]);

  const save = async () => {
    const problem = timingsProblem(rows);
    if (problem) { setError(problem); return; }
    setSaving(true);
    const failed = await onSave({ classTimings: rows.map(({ toDay, ...row }) => ({ ...row, ...(toDay && { toDay }) })) });
    setSaving(false);
    if (failed) setError(failed);
  };

  return (
    <VisitDialog
      titleId="ds-timings-title"
      title="Available Time & Date"
      className="ds-centre-edit ds-centre-edit--timings"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ds-btn ds-btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
        </>
      )}
    >
      <CentreSummary centre={centre}>
        <small>Same as the Available Class Timing on the Balagruha form; saving here changes it there too.</small>
      </CentreSummary>
      <div className="ds-timing-edit" role="group" aria-label="Class timings">
        <div className="ds-timing-edit__head" aria-hidden="true">
          <span>From Day <em>*</em></span>
          <span>To Day</span>
          <span>Start Time <em>*</em></span>
          <span>End Time <em>*</em></span>
          <span />
        </div>
        {rows.map((row, i) => {
          const n = i + 1;
          return (
            <div className="ds-timing-edit__row" key={i}>
              <select aria-label={`From day, row ${n}`} value={row.day} onChange={(e) => setDay(i, e.target.value)}>
                <option value="">Select day</option>
                {WEEK_DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select aria-label={`To day, row ${n}`} title="Optional: the last day of a From–To range" value={row.toDay} disabled={!row.day || row.day === "Sunday"} onChange={(e) => update(i, { toDay: e.target.value })}>
                <option value="">—</option>
                {WEEK_DAYS.slice(WEEK_DAYS.indexOf(row.day) + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <NativePickerField type="time" label={`Start time, row ${n}`} icon={<Clock size={13} />} className={row.startTime ? "" : "is-empty"} value={row.startTime} display={row.startTime ? ampm(row.startTime) : "Select time"} onChange={(startTime) => update(i, { startTime })} />
              <NativePickerField type="time" label={`End time, row ${n}`} icon={<Clock size={13} />} className={row.endTime ? "" : "is-empty"} value={row.endTime} display={row.endTime ? ampm(row.endTime) : "Select time"} onChange={(endTime) => update(i, { endTime })} />
              <button type="button" className="ds-timing-edit__remove" aria-label={`Remove timing ${n}`} title="Remove" onClick={() => remove(i)}><Trash2 size={15} /></button>
            </div>
          );
        })}
        <button type="button" className="ds-timing-edit__add" onClick={() => edit([...rows, EMPTY_ROW])}><Plus size={14} />Add Timing</button>
      </div>
      {error && <p className="ds-centre-edit__error" role="alert">{error}</p>}
    </VisitDialog>
  );
}

export function TotalVisitsModal({ centre, thisWeek, counted, onClose, onSave }) {
  const [value, setValue] = useState(() => (Number.isFinite(centre.totalVisits) ? String(centre.totalVisits) : ""));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const text = value.trim();

  const save = async () => {
    if (text && !(/^\d+$/.test(text) && Number(text) <= MAX_TOTAL_VISITS)) {
      setError(`Enter a whole number from 0 to ${MAX_TOTAL_VISITS}, or leave it blank.`);
      return;
    }
    setSaving(true);
    const failed = await onSave({ totalVisits: text === "" ? null : Number(text) });
    setSaving(false);
    if (failed) setError(failed);
  };

  return (
    <VisitDialog
      titleId="ds-total-visits-title"
      title="Total Visits"
      className="ds-centre-edit ds-centre-edit--visits"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ds-btn ds-btn--primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
        </>
      )}
    >
      <CentreSummary centre={centre}>
        <small>Visits this week: {thisWeek} · Visits in the calendar (dates shown): {counted}</small>
      </CentreSummary>
      <div className="ds-field ds-total-visits">
        <span id="ds-total-visits-label">Total Visits</span>
        <input
          aria-labelledby="ds-total-visits-label"
          type="number"
          min="0"
          max={MAX_TOTAL_VISITS}
          step="1"
          inputMode="numeric"
          autoFocus
          placeholder={String(counted)}
          value={value}
          onChange={(e) => { setError(""); setValue(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
        />
        <small>Shown as {thisWeek}/{text === "" ? counted : text}. Leave it blank to count the visits in the calendar.</small>
      </div>
      {error && <p className="ds-centre-edit__error" role="alert">{error}</p>}
    </VisitDialog>
  );
}
